import {
  action$,
  dispatchAction,
  extractPayload,
  ofType,
  withNamespace,
} from '@ardoq/rxbeach';
import { NavigatorConfiguration, NavigatorLayoutState } from '../types';
import {
  clickedInNavigator,
  expandCollapseToggleClicked,
  selectionEnd,
} from '../actions/navigatorActions';
import { selectionToggle } from '@ardoq/component-selection';
import { Observable, of, tap, withLatestFrom } from 'rxjs';
import { NodeModel } from '../models/types';
import { WorkspaceNode } from '../models/WorkspaceNode';
import { ComponentNode } from '../models/ComponentNode';
import { ScenarioNode } from '../models/ScenarioNode';
import {
  targetManager,
  EXPANDER_CLASS_NAME,
  CLOSE_WORKSPACE_CLASS_NAME,
  CLOSE_SCENARIO_CLASS_NAME,
  CONTEXT_MENU_CLASS_NAME,
} from '../targetManager';
import { getDOMNodeInNavigatorWithDataId } from '../getDOMNodeInNavigatorWithDataId';
import { isMac } from '@ardoq/common-helpers';
import {
  COMPONENT_ITEM_CLASS_NAME,
  CREATE_COMPONENT_ITEM_CLASS_NAME,
  SCENARIO_ITEM_CLASS_NAME,
  WORKSPACE_ITEM_CLASS_NAME,
} from '../consts';
import { CREATE_COMPONENT_NODE_ID } from '../models/CreateComponentNode';
import React from 'react';
import { identity } from 'lodash';

export const getNavigatorClickRoutine = (
  navigator$: Observable<NavigatorLayoutState>,
  navigatorConfiguration: NavigatorConfiguration
) =>
  action$.pipe(
    navigatorConfiguration.actionNamespace
      ? withNamespace(navigatorConfiguration.actionNamespace)
      : identity,
    ofType(clickedInNavigator),
    extractPayload(),
    withLatestFrom(navigator$, navigatorConfiguration.isLinking$ ?? of(false)),
    tap(async ([event, { isDrag, tree }, isLinking]) => {
      const { eventHandlers, actionNamespace } = navigatorConfiguration;
      const { target, className } = targetManager.getTarget(event);
      const nodeId = target ? getDOMNodeInNavigatorWithDataId(target) : null;

      if (
        nodeId === CREATE_COMPONENT_NODE_ID &&
        className === CREATE_COMPONENT_ITEM_CLASS_NAME &&
        !isLinking &&
        !isDrag
      ) {
        const rawFilterTerm = tree.getRawFilterTerm();
        if (rawFilterTerm && eventHandlers.onCreateNewComponent) {
          eventHandlers.onCreateNewComponent(rawFilterTerm);
        }
        return;
      }

      const node = nodeId ? tree.getNode(nodeId) : null;
      if (!(target && className && nodeId && node) || isLinking || isDrag) {
        return;
      }

      if (className === EXPANDER_CLASS_NAME) {
        event.stopPropagation();
        return toggleNode(nodeId, actionNamespace);
      }

      if (
        [
          WORKSPACE_ITEM_CLASS_NAME,
          COMPONENT_ITEM_CLASS_NAME,
          SCENARIO_ITEM_CLASS_NAME,
        ].includes(className)
      ) {
        return changeSelectionOrOnItemClick(
          event,
          node,
          eventHandlers,
          actionNamespace
        );
      }

      if (className === CLOSE_WORKSPACE_CLASS_NAME) {
        return closeWorkspace(event, node, eventHandlers);
      }

      if (className === CLOSE_SCENARIO_CLASS_NAME) {
        return closeScenario(eventHandlers);
      }

      if (className === CONTEXT_MENU_CLASS_NAME) {
        return showContextMenu(eventHandlers, event.nativeEvent);
      }
    })
  );

const toggleNode = (nodeId: string, actionNamespace?: string) =>
  dispatchAction(expandCollapseToggleClicked({ nodeId }), actionNamespace);

const changeSelectionOrOnItemClick = (
  event: React.MouseEvent,
  node: NodeModel,
  eventHandlers: NavigatorConfiguration['eventHandlers'],
  actionNamespace?: string
) => {
  const isMetaKeyPressed = isMac() ? event.metaKey : event.ctrlKey;
  if (isMetaKeyPressed) {
    if (eventHandlers.onMetaKeyClick) {
      eventHandlers.onMetaKeyClick(node.id);
      return;
    }
    dispatchAction(selectionToggle({ nodeId: node.id }), actionNamespace);
    return;
  }
  if (event.shiftKey) {
    dispatchAction(selectionEnd({ nodeId: node.id }), actionNamespace);
    return;
  }
  return setContext(node, eventHandlers);
};

const closeWorkspace = (
  event: React.MouseEvent,
  node: NodeModel,
  eventHandlers: NavigatorConfiguration['eventHandlers']
) => {
  event.nativeEvent.stopImmediatePropagation();
  if (eventHandlers.onWorkspaceCloseClick) {
    eventHandlers.onWorkspaceCloseClick(node.id);
  }
};

const closeScenario = (
  eventHandlers: NavigatorConfiguration['eventHandlers']
) => eventHandlers.onScenarioCloseClick();

const showContextMenu = (
  eventHandlers: NavigatorConfiguration['eventHandlers'],
  event: MouseEvent
) => eventHandlers.onContextMenuClicked(event);

const setContext = (
  node: NodeModel,
  eventHandlers: NavigatorConfiguration['eventHandlers']
) => {
  if (node instanceof WorkspaceNode) {
    eventHandlers.onWorkspaceClick(node.id);
  } else if (node instanceof ComponentNode) {
    eventHandlers.onComponentClick(node.id);
  } else if (node instanceof ScenarioNode) {
    eventHandlers.onScenarioClick(node.id);
  }
};
